import React, { useState, useEffect } from 'react';
import { Bell, Check, Radio, Award, MessageSquare, Trash2, ChevronRight, Settings, BrainCircuit, BarChart3, Users, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '../utils';
import { api } from '../api';
import ConfirmModal from '../components/ConfirmModal';
import EmptyState from '../components/EmptyState';

const NotificationItem = ({ id, type, title, desc, time, isUnread, path, onMarkRead }) => {
  const configs = {
    live: { icon: Radio, color: "text-red-500", bg: "bg-red-50", label: "Live Room" },
    brainstorm: { icon: BrainCircuit, color: "text-green-500", bg: "bg-green-50", label: "Ideation" },
    poll: { icon: BarChart3, color: "text-brand-indigo", bg: "bg-brand-indigo/10", label: "Community Vote" },
    community: { icon: Users, color: "text-indigo-500", bg: "bg-indigo-50", label: "Community" },
    badge: { icon: Award, color: "text-yellow-600", bg: "bg-yellow-50", label: "Achievement" },
    qa: { icon: MessageSquare, color: "text-purple-500", bg: "bg-purple-50", label: "Q&A Reply" }
  };

  const config = configs[type] || configs.community;
  const Icon = config.icon;

  return (
    <Link 
      to={path || "#"} 
      onClick={() => isUnread && onMarkRead(id)}
      className={cn(
        "p-6 flex gap-4 transition-all border-b border-gray-50 last:border-0 hover:bg-gray-50 active:scale-[0.99] cursor-pointer group",
        isUnread ? "bg-indigo-50/20" : "bg-white"
      )}
    >
      <div className={cn(
        "w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 shadow-sm transition-transform group-hover:scale-110",
        config.bg, config.color
      )}>
        <Icon size={22} className={type === 'live' ? "animate-pulse" : ""} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex justify-between items-start mb-1">
          <div className="flex items-center gap-2">
             <span className={cn("text-xxxs font-bold uppercase tracking-[0.15em] px-1.5 py-0.5 rounded-md", config.bg, config.color)}>
                {config.label}
             </span>
             <h3 className={cn("text-sm font-bold truncate", isUnread ? "text-gray-900" : "text-gray-500")}>{title}</h3>
          </div>
          <span className="text-xxs font-bold text-gray-300 uppercase tracking-[0.15em] shrink-0 ml-4">{time}</span>
        </div>
        <p className="text-sm text-gray-500 font-medium leading-relaxed line-clamp-2">{desc}</p>
      </div>
      <div className="flex items-center gap-4">
        {isUnread && <div className="w-2 h-2 bg-indigo-600 rounded-full shadow-lg shadow-indigo-200"></div>}
        <ChevronRight size={18} className="text-gray-200 group-hover:text-indigo-400 group-hover:translate-x-1 transition-all" />
      </div>
    </Link>
  );
};

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('All');
  const [confirmConfig, setConfirmConfig] = useState({ isOpen: false });

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const data = await api.get('/notifications');
      setNotifications(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch notifications', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const handleMarkRead = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      setNotifications(prev => Array.isArray(prev) ? prev.map(n => n.id === id ? { ...n, is_read: true } : n) : []);
    } catch (err) {
      console.error('Failed to mark as read', err);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await api.post('/notifications/read-all');
      setNotifications(prev => Array.isArray(prev) ? prev.map(n => ({ ...n, is_read: true })) : []);
    } catch (err) {
      console.error('Failed to mark all as read', err);
    }
  };

  const handleClearAll = async () => {
    setConfirmConfig({
      isOpen: true,
      title: 'Clear Inbox?',
      description: 'Are you sure you want to clear all notifications? This action cannot be undone.',
      confirmText: 'Yes, Clear All',
      onConfirm: async () => {
        try {
          await api.delete('/notifications');
          setNotifications([]);
          setConfirmConfig({ isOpen: false });
        } catch (err) {
          console.error('Failed to clear notifications', err);
        }
      }
    });
  };

  const filteredNotifications = (Array.isArray(notifications) ? notifications : []).filter(n => {
    if (filter === 'Unread') return !n.is_read;
    if (filter === 'Live') return n.type === 'live';
    if (filter === 'Boards') return n.type === 'brainstorm';
    return true;
  });

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12">
      <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-10 text-center md:text-left">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight flex items-center gap-3 justify-center md:justify-start">
            <Bell className="text-indigo-600" size={32} />
            Inbox
          </h1>
          <p className="text-gray-500 font-medium mt-1">Updates from your communities and live interactions.</p>
        </div>
        <div className="flex items-center gap-3">
          <button 
            onClick={handleMarkAllRead}
            className="bg-white border border-gray-100 p-3 rounded-2xl text-gray-400 hover:text-indigo-600 transition-all shadow-sm group"
            title="Mark all as read"
          >
            <Check size={20} />
          </button>
          <Link to="/settings" className="bg-white border border-gray-100 p-3 rounded-2xl text-gray-400 hover:text-indigo-600 transition-all shadow-sm">
            <Settings size={20} />
          </Link>
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden shadow-2xl shadow-indigo-100/20">
        <div className="p-6 bg-gray-50/50 border-b border-gray-100 flex items-center justify-between">
          <div className="flex gap-2">
            {['All', 'Unread', 'Live', 'Boards'].map((f) => (
              <button 
                key={f}
                onClick={() => setFilter(f)}
                className={cn(
                  "px-4 py-2 rounded-xl text-xxs font-bold uppercase tracking-[0.15em] transition-all active:scale-95",
                  filter === f ? "bg-white text-indigo-600 shadow-sm border border-indigo-100" : "text-gray-400 hover:text-gray-600"
                )}
              >
                {f}
              </button>
            ))}
          </div>
          <button 
            onClick={handleClearAll}
            className="text-xxs font-bold text-gray-400 uppercase tracking-[0.15em] hover:text-red-600 transition-colors flex items-center gap-1.5 active:scale-95"
          >
            <Trash2 size={12} />
            Clear
          </button>
        </div>

        <div className="divide-y divide-gray-50">
          {loading ? (
            <div className="p-20 flex flex-col items-center justify-center text-gray-400">
              <Loader2 size={40} className="animate-spin mb-4 text-indigo-600" />
              <p className="font-bold uppercase tracking-[0.15em] text-xs">Loading Inbox...</p>
            </div>
          ) : (Array.isArray(filteredNotifications) && filteredNotifications.length > 0) ? (
            filteredNotifications.map(notification => (
              <NotificationItem 
                key={notification.id}
                id={notification.id}
                type={notification.type}
                title={notification.title}
                desc={notification.description}
                time={formatTime(notification.created_at)}
                isUnread={!notification.is_read}
                path={notification.path}
                onMarkRead={handleMarkRead}
              />
            ))
          ) : (
            <div className="p-12">
              <EmptyState 
                icon={Bell}
                title="Inbox is empty"
                description={filter === 'All' ? "You're all caught up! Updates from your communities will appear here." : `No ${filter.toLowerCase()} notifications found.`}
              />
            </div>
          )}
        </div>

        {Array.isArray(notifications) && notifications.length > 0 && (
          <div className="p-8 text-center bg-gray-50/30">
            <button className="text-xs font-bold text-gray-400 hover:text-indigo-600 transition-all uppercase tracking-[0.15em] active:scale-95">
              Load More Activity
            </button>
          </div>
        )}
      </div>

      <ConfirmModal 
        {...confirmConfig}
        onClose={() => setConfirmConfig({ ...confirmConfig, isOpen: false })}
      />
    </div>
  );
};

export default Notifications;
